'use client';

import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { useLocale, useTranslations } from 'next-intl';

import { useHitl } from '@/components/assistant-ui/hitl/hitl-provider';
import { useToolPhaseReady } from '@/components/assistant-ui/hitl/tool-phase-state';
import {
  parseHitlCallKey,
  useHitlCallKey,
  useWaitingOnGuest,
} from '@/components/assistant-ui/hitl/use-hitl-call';
import {
  spliceHitlStep,
  withGuestWaits,
} from '@/components/assistant-ui/thread/activity-trail-helpers';
import { buildTrailTimeline } from '@/components/assistant-ui/thread/build-trail-timeline';
import { CollapsingTrail } from '@/components/assistant-ui/thread/collapsing-trail';
import { createTrailInProgressIconRenderer } from '@/components/assistant-ui/thread/rotating-trail-icon';
import { useTextRevealing } from '@/components/assistant-ui/thread/text-reveal';
import {
  ElapsedTimer,
  LiveStepElapsed,
  StepElapsed,
} from '@/components/assistant-ui/thread/trail-elapsed';
import type { TimelineRow } from '@/components/assistant-ui/thread/trail-records';
import { STATUS_TRANSITION_MS } from '@/components/assistant-ui/thread/trail-step-icon';
import { deriveTimeAnchors, readCreatedAt } from '@/components/assistant-ui/thread/trail-timing';
import {
  buildPickFinal,
  resolveTrailVariants,
  safeT,
  type TrailVariantOverrides,
} from '@/components/assistant-ui/thread/trail-variants';
import { useMergedStepTimings } from '@/components/assistant-ui/thread/use-merged-step-timings';
import { useMetadataTrailTimings } from '@/components/assistant-ui/thread/use-metadata-trail-timings';
import { useStepRecords } from '@/components/assistant-ui/thread/use-step-records';
import { useTickNow } from '@/components/assistant-ui/thread/use-tick-now';
import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { readDestinations } from '@/components/assistant-ui/tool-groups/trail-arg-readers';
import { useConfigValue } from '@/components/providers/config-provider';
import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { useDestinations } from '@/hooks/use-destinations';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useResolvedThinkingIndicatorText } from '@/hooks/use-resolved-thinking-indicator-text';
import {
  getMessageById,
  useAssistantCloudStore,
} from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { useLiveTrailTimings } from '@/lib/assistant-cloud-local/stores/live-trail-timings';
import { useHistorySession } from '@/lib/history-key/session';
import { getThreadUuid } from '@/lib/utils/thread';

import { useTurnStopped } from './stopped-turns';

export function ActivityTrail() {
  const t = useTranslations();
  const readOnly = useReadOnly();
  const { isDebugMode } = useDebugMode();
  const { getTrailConfig } = useToolGroup();

  // The trail is always on; this only reads `showInHistory`, a per-tenant
  // display tweak for read-only history views.
  const trailToggle = useConfigValue<{ showInHistory?: boolean }>('activityTrail');
  const trailConfig = useLocalizedConfig<TrailVariantOverrides>('chat.activityTrail');

  const messageId = useAuiState(({ message }) => message?.id);
  const isMessageRunning = useAuiState(({ message }) => message?.status?.type === 'running');
  const textRevealing = useTextRevealing();
  const stopped = useTurnStopped(messageId);
  const hitlModule = useHitl();
  // Use message status for suspension; the call key only tracks identity and outcome.
  const {
    toolCallId: hitlCallId,
    resultStatus: hitlStatus,
    lane: hitlLane,
  } = parseHitlCallKey(useHitlCallKey());
  // A run parked on the guest is not finished, so the trail must not collapse into its summary:
  // the resume would swap it back out of the panel, remounting every row and replaying its entry
  // animation.
  const waitingOnGuest = useWaitingOnGuest();
  // Whether the card has finished its pre-approval fetch — drives the "fetching" → "awaiting
  // approval" phase of the merged HITL step. Approval-lane cards have no pre-fetch: always ready.
  const hitlPhaseReady = useToolPhaseReady(hitlCallId);
  const hitlLoaded = hitlLane === 'approval' || hitlPhaseReady;

  const storedMessage = useAssistantCloudStore(state =>
    messageId ? getMessageById(state.messages, messageId) : undefined,
  );
  const storeStreamDurationMs = storedMessage?.stream_duration_ms;
  const storeStepTimings = storedMessage?.step_timings;
  const storeUpdatedAtMs = readCreatedAt(storedMessage?.updated_at);

  // Prefer persisted `created_at` (backdated to the real stream start) over
  // the runtime's load-time `message.createdAt`, otherwise step offsets
  // would come out negative after a cloud rehydration.
  const rawCreatedAtMs = useAuiState(({ message }) => readCreatedAt(message?.createdAt));
  const persistedCreatedAtMs = readCreatedAt(storedMessage?.created_at);
  const createdAtMs = persistedCreatedAtMs ?? rawCreatedAtMs;

  // Fallback duration when `stream_duration_ms` was never persisted: width
  // between the backdated `created_at` and the `updated_at` set when the
  // message landed in the store. Approximate — `updated_at` advances on
  // later edits — and only meaningful when the two diverge (equal values
  // mean neither was set from a real timing signal).
  const derivedDurationMs =
    persistedCreatedAtMs !== undefined &&
    storeUpdatedAtMs !== undefined &&
    storeUpdatedAtMs > persistedCreatedAtMs
      ? storeUpdatedAtMs - persistedCreatedAtMs
      : undefined;

  // Read-only-host fallback (e.g. bazak-os-platform).
  const { stepTimings: metadataStepTimings, durationMs: metadataDurationMs } =
    useMetadataTrailTimings({ isDebugMode });

  // Captured once on mount as the fallback messageStartAt — see deriveTimeAnchors.
  const [trailMountedAt] = useState(() => Date.now());

  // Match the threadId the transport uses so the live mirror lookup hits.
  const threadIdRaw = useAuiState(
    ({ threadListItem }) =>
      (threadListItem as { remoteId?: string; id?: string } | undefined)?.remoteId ??
      (threadListItem as { id?: string } | undefined)?.id,
  );
  const threadId = threadIdRaw ? getThreadUuid(threadIdRaw) : undefined;
  const liveMirror = useLiveTrailTimings(threadId);

  const locale = useLocale();
  const tenantKey = useConfigValue<string>('tenantKey') ?? undefined;

  // Joined string keeps `useAuiState` stable across re-renders — returning
  // an array literal would re-trigger every state change. We split back to an
  // array for `useDestinations`, which itself keys on the sorted-joined codes.
  const destinationCodesKey = useAuiState(({ message }) => {
    const parts = message?.content as
      readonly { type: string; toolName?: string; args?: unknown }[] | undefined;
    if (!parts?.length) return '';
    const codes = new Set<string>();
    for (const part of parts) {
      if (part.type !== 'tool-call') continue;
      const config = part.toolName ? getTrailConfig?.(part.toolName) : undefined;
      const extracted = config?.extractCodes?.(part.args) ?? readDestinations(part.args);
      for (const code of extracted) codes.add(code);
    }
    return [...codes].sort().join(',');
  });
  const destinationCodes = useMemo(
    () => (destinationCodesKey ? destinationCodesKey.split(',') : []),
    [destinationCodesKey],
  );
  const { destinations: destinationsMap } = useDestinations(destinationCodes);

  const { stepRecords: rawStepRecords, iconsByStepId: rawIcons } = useStepRecords({
    isMessageRunning: !!isMessageRunning,
    textRevealing,
    trailConfig: trailConfig ?? undefined,
    getTrailConfig,
    t,
    locale,
    tenantKey,
    destinationsMap,
  });

  // A HITL tool is one call shown as a single evolving step ("awaiting approval" while the call
  // is unresolved, then the persisted outcome). The tenant's HITL module supplies the steps via
  // `getTrailConfig`; approval tools without a custom config keep the raw row, which already
  // carries the awaiting-approval → running → done/denied statuses via `classifyToolPart`.
  // A stable row id keeps it as ONE row that updates in place.
  const hitlGetTrailConfig = hitlModule.getTrailConfig;
  const hitlGuestWaitOf = hitlModule.guestWaitOf;
  const signedIn = useHistorySession(state => state.signedIn === true);
  const { stepRecords, iconsByStepId } = useMemo(() => {
    if (!hitlGetTrailConfig) return { stepRecords: rawStepRecords, iconsByStepId: rawIcons };
    const records = withGuestWaits({
      records: rawStepRecords,
      waitsForOf: function toolkitWaitOf(record) {
        return hitlGuestWaitOf?.({ toolName: record.toolName ?? '', signedIn });
      },
    });
    const unchanged = { stepRecords: records, iconsByStepId: rawIcons };
    if (!hitlCallId) return unchanged;
    const idx = records.findIndex(r => r.kind === 'tool' && r.id === hitlCallId);
    if (idx === -1) return unchanged;
    const subs = hitlGetTrailConfig({
      toolName: records[idx].toolName ?? '',
      toolCallId: hitlCallId,
      resultStatus: hitlStatus || undefined,
      awaiting: !hitlStatus,
      loaded: hitlLoaded,
      // A run parked on the guest has nothing loading, and the timeline draws a loader there as
      // done: the row waits on the guest from the first frame, before the card reports in.
      cardShown: hitlPhaseReady || !isMessageRunning,
      signedIn,
      t,
    });
    if (!subs?.length) return unchanged;
    return spliceHitlStep({ records, icons: rawIcons, index: idx, step: subs[0] });
  }, [
    rawStepRecords,
    rawIcons,
    hitlCallId,
    hitlStatus,
    hitlLoaded,
    hitlPhaseReady,
    isMessageRunning,
    signedIn,
    hitlGetTrailConfig,
    hitlGuestWaitOf,
    t,
  ]);

  // Resolved = the user acted. The run keeps streaming the acknowledgement, so the trail stays
  // live — but the initial "thinking" row must not re-spin (see keepInitialThinkingComplete).
  const hitlResolved = hitlCallId !== '' && !!hitlStatus;

  const stepTimings = useMergedStepTimings({
    liveMirror,
    storeStepTimings,
    metadataStepTimings,
  });

  const totalDurationMs = storeStreamDurationMs ?? metadataDurationMs ?? derivedDurationMs;
  const requestStartedAt = liveMirror?.requestStartedAt;
  const liveFinishedAt = useOwnFinishedAt({
    running: !!isMessageRunning,
    finishedAt: liveMirror?.finishedAt,
  });

  // The transport flags `finishedAt` one frame before assistant-ui's
  // `isMessageRunning` flips false. Treating that gap as not-live avoids
  // a phantom trailing-thinking row flashing for one render at end-of-stream.
  // Also live while the answer is still typing out on screen: the trail must not add its closing
  // row above it, or fold, until the text has finished appearing.
  const isLive = (!!isMessageRunning && liveFinishedAt === undefined) || textRevealing;

  // On the live→finished flip, hold the final "done" row back for one icon-crossfade cycle so the
  // preceding steps land their spinner→checkmark first — otherwise "Response finished" pops in
  // while the step above is still mid-transition. Only fires on a real transition within this mount;
  // a history render (never live) shows the final row immediately.
  //
  // BUT skip the hold when a live trailing "thinking" row was already showing: that row morphs in
  // place to the final label via its stable gap id (see build-trail-timeline), so suppressing it
  // would remove an already-visible row and flicker it out→in. The hold is only for the case where
  // the final row would be a *fresh* insertion (last real step still crossfading).
  // `liveTrailingRowIdRef` holds the previous render's live trailing row (updated by the effect after
  // `rows` below); the flip effect runs before it, so on the flip it reads the last live render's value.
  const [finalRowHeld, setFinalRowHeld] = useState(false);
  // Same idea for every step, not only the last: see the settled-row count after `rows`.
  const [stepLanding, setStepLanding] = useState<number | undefined>();
  useEffect(() => {
    if (stepLanding === undefined) return;
    const timer = setTimeout(() => setStepLanding(undefined), STATUS_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [stepLanding]);
  const wasLiveRef = useRef(isLive);
  const liveTrailingRowIdRef = useRef<string | undefined>(undefined);
  // Layout effect: decided before paint, or the frame that ends the run paints the final row and
  // the hold then pulls it back out, pushing the answer down and up again.
  useLayoutEffect(() => {
    const wasLive = wasLiveRef.current;
    wasLiveRef.current = isLive;
    if (wasLive && !isLive && liveTrailingRowIdRef.current === undefined) {
      setFinalRowHeld(true);
      const timer = setTimeout(() => setFinalRowHeld(false), STATUS_TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [isLive]);

  // Subscribe to the 100 ms tick only while the trail is actively live.
  // History view (most messages, most of the time) sees a frozen value and
  // doesn't re-render on the interval — without this, every mounted finished
  // trail would re-render 10×/sec, fighting the expand/collapse height
  // animation (especially noticeable with React DevTools open).
  const tickNow = useTickNow({ enabled: isLive });

  const { messageStartAt, messageEndAt } = useMemo(
    () =>
      deriveTimeAnchors({
        isMessageRunning: isLive,
        trailMountedAt,
        requestStartedAt,
        createdAtMs,
        totalDurationMs,
        liveFinishedAt,
        stepTimings,
        now: tickNow,
      }),
    [
      isLive,
      trailMountedAt,
      requestStartedAt,
      createdAtMs,
      totalDurationMs,
      liveFinishedAt,
      stepTimings,
      tickNow,
    ],
  );

  const variants = useMemo(
    () => resolveTrailVariants({ overrides: trailConfig ?? undefined, t }),
    [trailConfig, t],
  );

  const fallbackPreparing =
    variants.preparing.length > 0 ? variants.preparing[0] : t('rotatingToolUI.loading');
  const personalizedThinking = useResolvedThinkingIndicatorText({ requireOverride: true });
  const thinkingLabel =
    personalizedThinking ??
    safeT({ t, key: 'activityTrail.thinking', fallback: fallbackPreparing });
  const fallbackFinalSingle = safeT({
    t,
    key: 'activityTrail.streamingDone',
    fallback: '',
  });

  const trailOverrides = trailConfig ?? undefined;
  const { resolveDoneLabel, bucketCatalogNamespace } = useToolGroup();
  const pickFinal = useMemo(
    () =>
      buildPickFinal({
        resolveDoneLabel,
        stepRecords,
        t,
        fallbackFinalSingle,
        trailOverrides,
        catalogNamespace: bucketCatalogNamespace,
      }),
    [stepRecords, t, fallbackFinalSingle, trailOverrides, resolveDoneLabel, bucketCatalogNamespace],
  );

  const { rows, totalMs } = useMemo(() => {
    const built = buildTrailTimeline({
      stepRecords,
      stepTimings,
      messageStartAt,
      messageEndAt,
      isMessageRunning: isLive,
      // Suppress the placeholder trailing "thinking" row while awaiting the user (the card is the
      // loader). NOT after they act: the acknowledgement is an LLM stream with a real gap before its
      // first token, so the trailing loader is the only progress signal during that wait. Also
      // suppress it during the post-finish hold so the final "done" row waits for the checkmarks.
      suppressTrailingRow: waitingOnGuest || finalRowHeld || stepLanding !== undefined,
      // After the user acts, keep the initial thinking row completed so it doesn't re-spin (the
      // HITL step has no timing, which would otherwise re-trigger it).
      keepInitialThinkingComplete: hitlResolved,
      shownTrailingRowId: liveTrailingRowIdRef.current,
      stopped,
      // A client-side tool parks the run while the browser does its work, such as reading the
      // values a card shows; an approval gate parks it with nothing loading.
      clientWorkInFlight: waitingOnGuest && hitlLane === 'human',
      labels: {
        thinking: thinkingLabel,
        preparing: variants.preparing,
        longWait: variants.longWait,
        pickFinal,
      },
    });
    return built;
  }, [
    stepRecords,
    stepTimings,
    messageStartAt,
    messageEndAt,
    isLive,
    waitingOnGuest,
    hitlResolved,
    finalRowHeld,
    stepLanding,
    stopped,
    hitlLane,
    thinkingLabel,
    variants,
    pickFinal,
  ]);

  // Record the live trailing "thinking" loader this render shows as the last row, if any.
  // The flip effect above reads the PREVIOUS render's value (it runs before this effect),
  // so on the live→done flip it sees the last live render's state and skips the hold-back
  // when that row will simply morph into the final label (no suppress → no flicker).
  useEffect(() => {
    const last = rows[rows.length - 1];
    liveTrailingRowIdRef.current =
      isLive && last?.kind === 'thinking' && last?.status === 'in-progress' ? last.id : undefined;
  });

  // A step that just finished crossfades its loader into its outcome. The placeholder row below it
  // waits until the outcome is in, so the next step never slides in beside one still finishing.
  // Real tool rows are never held: parallel calls still arrive together. Set during render so the
  // placeholder is never painted and then pulled; hiding it changes no settled row, so it settles.
  const settledRows = rows.filter(isSettledRow).length;
  const [shownSettledRows, setShownSettledRows] = useState(settledRows);
  if (settledRows !== shownSettledRows) {
    setShownSettledRows(settledRows);
    if (isLive && settledRows > shownSettledRows) setStepLanding(settledRows);
  }

  // True when at least one authoritative timing source exists. Per-row and
  // total elapsed render only when this is true — avoids a misleading "0s"
  // for messages whose timings were never captured. Step count stays visible
  // regardless (it's always meaningful when rows exist).
  const hasReliableElapsed =
    !!isMessageRunning ||
    liveMirror !== undefined ||
    totalDurationMs !== undefined ||
    Object.keys(stepTimings).length > 0;

  const renderInProgressIcon = useMemo(
    () => createTrailInProgressIconRenderer({ iconsByStepId }),
    [iconsByStepId],
  );

  // Takes the row directly so the closure doesn't depend on `rows` — keeps
  // its reference stable across tickNow updates, which is what lets memo'd
  // TrailRow skip re-renders for unchanged rows.
  const renderStepTrailing = useMemo(() => {
    if (!isDebugMode || !hasReliableElapsed) return undefined;
    return function renderStepTrailing(row: TimelineRow): ReactNode | undefined {
      if (row.liveAnchor !== undefined) return <LiveStepElapsed anchor={row.liveAnchor} />;
      return <StepElapsed elapsedMs={row.durationMs} />;
    };
  }, [isDebugMode, hasReliableElapsed]);

  if (messageId === undefined) return null;
  if (readOnly && trailToggle?.showInHistory === false) return null;
  if (rows.length === 0) return null;

  // Step count + total elapsed are debug-only. The user-facing progress
  // signal is the loader → checkmark; raw seconds are diagnostic. Step
  // count sits at the logical start, elapsed at the end (RTL-aware via
  // `justify-between`). `px-3` matches the row's outer padding so the
  // step count aligns with the row icons above it.
  const timerNode = isDebugMode ? (
    <div className="flex items-center justify-between px-3 pt-1 pb-2 text-xs text-muted-foreground tabular-nums">
      <span>{t('activityTrail.summaryStepCount', { count: rows.length })}</span>
      {hasReliableElapsed ? <ElapsedTimer elapsedMs={totalMs} isLive={isLive} /> : null}
    </div>
  ) : null;

  return (
    <CollapsingTrail
      rows={rows}
      isLive={isLive}
      waitingOnGuest={!readOnly && waitingOnGuest}
      renderInProgressIcon={renderInProgressIcon}
      renderStepTrailing={renderStepTrailing}
      timerNode={timerNode}
    />
  );
}

/**
 * A resumed run continues the same message, and until the transport opens the new request the live
 * mirror still holds the parked stream's finish stamp, which would show the run finished for a few
 * frames between the answer and the resume.
 */
function useOwnFinishedAt({
  running,
  finishedAt,
}: {
  running: boolean;
  finishedAt: number | undefined;
}): number | undefined {
  const [run, setRun] = useState<{ running: boolean; inherited?: number }>({ running });
  if (run.running !== running) setRun({ running, inherited: running ? finishedAt : undefined });
  return finishedAt === run.inherited ? undefined : finishedAt;
}

function isSettledRow(row: TimelineRow): boolean {
  return row.status === 'completed' || row.status === 'failed' || row.status === 'cancelled';
}
